/**
 * clinic-hours.js — clinic-edit 三段式營業時間互動（方向 A：動態增減時段）
 *
 * 對照 HTML（src/pages/admin/clinic-edit.html「CARD 2 — 營業時間」）：
 *   <form class="admin-clinic-form" novalidate>
 *     桌機 <div class="hours-table" data-hours="table"> … 七列 .hours-table__row[data-day]
 *     手機 <div class="hours-cards" data-hours="cards"> … 七卡 .hours-card[data-day]
 *
 * 資料模型：每天 mode ∈ {open 營業 / closed 公休 / appt 預約制}（radio 已存在）；
 *   open 含 1–3 個 segment {start,end}。段標籤中性「時段 N」，a11y aria-label 補語境
 *   （「週一 第1個時段 開始時間」）。
 *
 * 行為（dawa 三段式規格 — 方向 A）：
 *   - 每天預設 1 段；「＋新增時段」鈕 <3 段顯示、=3 段隱藏（移除鈕，非 disabled）。
 *   - 新增段：append 空段 + 進場（opacity+translateY，reduced-motion 瞬顯）、focus 落新段
 *     第一個 select、aria-live 宣告。
 *   - 移除鈕（×）：段數 ≥2 每段顯示，=1 隱藏（要關整天用「公休」radio）。空段直接移除；
 *     有值段用 inline-undo（AdminToast「已移除時段N」+ 復原 action，不彈 Modal）。
 *   - mode 切 公休/預約制 → 所有段 disabled + 淡出（.is-closed）、隱藏「＋新增時段」；
 *     預約制顯「依預約」note；切回營業復現 ≥1 段。
 *   - 每天（週二起）「複製上一天」ghost link：複製上一天 mode + 所有段 + highlight 回饋。
 *   - CARD 標題列「套用至平日（週一→五）」「套用至全部」：以週一為來源套用，破壞性 → inline-undo。
 *
 * 驗證（與 form-validation-ux-spec 一致，submit 時跑）：
 *   - 每段 end>start；同日多段不重疊；submit 前自動依 start 升序排序段；mode=open 至少 1 段；
 *     start/end 必選；公休/預約制段跳驗。error 紅字用 --admin-color-danger-text #B91C1C。
 *
 * 雙 DOM 提交衝突（form-validation spec §7.2，務必處理）：
 *   桌機 / 手機兩套 DOM 同時在 form 內、name 不同（桌機 hours-mon、手機 hours-mon-c）。
 *   submit 時把當前隱藏（display:none）那套整組 disabled（disabled 不進 submit），只送可見套。
 *   兩套以 data-day 對映、單一資料源（writeState）雙向同步，避免送兩份營業時間。
 *
 * name index：段用 hours-{day}-seg{N}-start/end（手機 hours-{day}-c-seg{N}-…），動態增減重編 index。
 * D8 時間清單：30 分一格、06:00–23:30、不支援跨夜（generateTimeOptions）。
 *
 * 載入：由 /src/admin.js 中央 import 鏈 auto-init（無對應 DOM [data-hours] 即 no-op）。
 * 與 admin-form-edit.js 共存的 submit 時序（capture vs bubble）見 admin-form-edit.js 檔頭說明。
 *
 * 沿用既有慣例：欄位 invalid 標記收斂至 field-error.js（單一真相源）、AdminToast.action
 * undo、prefersReducedMotion。
 *
 * 註：hours 的 select 位於 .hours-table__seg（非 .form-field 包裹），故 field-error 的
 *   setInvalid(select)/clearInvalid(select) 對它只切 aria-invalid（closest('.form-field')=null →
 *   不加 form-field--invalid、不建 error 節點），與原本 setSelectInvalid/clearSelectInvalid 行為等價。
 *   段內 / 跨段錯誤文字仍由本檔 renderErrors 自建 .hours-table__error 節點（與 form-field atom 無關）。
 */

import { setInvalid, clearInvalid } from './field-error.js';
import { prefersReducedMotion } from '../utils/dom.js';

const DAYS = [
  { key: 'mon', label: '週一' },
  { key: 'tue', label: '週二' },
  { key: 'wed', label: '週三' },
  { key: 'thu', label: '週四' },
  { key: 'fri', label: '週五' },
  { key: 'sat', label: '週六' },
  { key: 'sun', label: '週日' },
];
/* day-group 常數集中於此（套用 scope 的唯一日期判定來源；CSS 不得再依週幾判定）。 */
const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri'];
const WEEKEND_DAYS = ['sat', 'sun'];
const MAX_SEGMENTS = 3;

/* D8 預設時間清單：30 分一格、06:00–23:30、不支援跨夜。需客戶確認。 */
function generateTimeOptions() {
  const out = [];
  for (let h = 6; h <= 23; h += 1) {
    for (let m = 0; m < 60; m += 30) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      out.push(`${hh}:${mm}`);
    }
  }
  return out; // 06:00 … 23:30
}
const TIME_OPTIONS = generateTimeOptions();

/* 欄位 invalid 標記 → 收斂至 field-error.js（檔頭 import）。hours select 無 .form-field 包裹，
 * 故 setInvalid(select)/clearInvalid(select) 只切 aria-invalid，等價原 setSelectInvalid/clearSelectInvalid。 */

/* "HH:MM" → 分鐘整數（排序 / 比較用） */
function toMinutes(value) {
  if (!value) return null;
  const [h, m] = value.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

/* 帶時段（顯示 select + 允許多段 + 顯示「＋新增時段」）的模式：營業 / 預約制。
 * 預約制（appt）保留營業時間，與營業同渲染路徑，僅多掛常駐「需預約」標記；唯公休（closed）
 * 收合成一句話。散落的 mode==='open' 判斷一律改用此 helper，讓 appt 也走帶段路徑。 */
function hasSegments(mode) {
  return mode === 'open' || mode === 'appt';
}

/* =====================================================================
 * HoursDay — 單一「天」的控制器，同時操作桌機 row 與手機 card 兩個視圖。
 * 單一資料源 = this.segments（[{start,end}]）+ this.mode；writeView 把資料源
 * 渲染進兩個視圖的 DOM，避免雙 DOM 不同步。
 * ===================================================================== */
class HoursDay {
  /**
   * @param {object} cfg
   * @param {{key,label}} cfg.day
   * @param {HTMLElement|null} cfg.tableRow  桌機 .hours-table__row（data-day）
   * @param {HTMLElement|null} cfg.card      手機 .hours-card（data-day）
   * @param {function} cfg.onUndoToast       inline-undo toast helper
   */
  constructor({ day, tableRow, card, onUndoToast }) {
    this.day = day;
    this.tableRow = tableRow;
    this.card = card;
    this.onUndoToast = onUndoToast;
    /** @type {Array<{start:string,end:string}>} 單一資料源 */
    this.segments = [];
    this.mode = 'open';
    /** 進場動畫只在「使用者新增段」時觸發，初始渲染不觸發 */
    this._animateNextRender = false;
  }

  /* 從現有 HTML（伺服器渲染的初值）讀 mode + 段，建立資料源 */
  hydrateFromDom() {
    // mode：以桌機 radiogroup 為來源（手機為鏡像）
    const checked =
      this.tableRow?.querySelector('.hours-table__status input:checked') ||
      this.card?.querySelector('.hours-table__status input:checked');
    this.mode = checked ? checked.value : 'open';

    // 段：讀桌機既有 select 對（舊結構為單組 start/end）
    const segs = [];
    const timeWrap = this.tableRow?.querySelector('.hours-table__time');
    if (timeWrap) {
      const starts = timeWrap.querySelectorAll('select[data-role="start"], select[name*="start"]');
      const ends = timeWrap.querySelectorAll('select[data-role="end"], select[name*="end"]');
      const n = Math.min(starts.length, ends.length);
      for (let i = 0; i < n; i += 1) {
        segs.push({ start: starts[i].value || '', end: ends[i].value || '' });
      }
    }
    this.segments = segs.length ? segs : [{ start: '', end: '' }];
    if (this.mode !== 'open' && this.segments.length === 0) {
      this.segments = [{ start: '', end: '' }];
    }
  }

  /* ---- DOM 生成 ---- */
  buildSelect(role, segIndex, view) {
    const select = document.createElement('select');
    select.className = 'form-field__input form-field__input--select';
    select.dataset.role = role; // start | end
    select.dataset.seg = String(segIndex);
    const suffix = view === 'card' ? '-c' : '';
    const id = `hours-${this.day.key}${suffix}-seg${segIndex + 1}-${role}`;
    select.id = id;
    select.name = id;
    const ordinal = segIndex + 1;
    const roleLabel = role === 'start' ? '開始時間' : '結束時間';
    select.setAttribute('aria-label', `${this.day.label} 第${ordinal}個時段 ${roleLabel}`);

    // 空占位（未選）+ 完整時間 option
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = '請選擇';
    select.appendChild(placeholder);
    TIME_OPTIONS.forEach((t) => {
      const opt = document.createElement('option');
      opt.value = t;
      opt.textContent = t;
      select.appendChild(opt);
    });
    return select;
  }

  buildSegment(seg, segIndex, view) {
    const wrap = document.createElement('div');
    wrap.className = 'hours-table__seg';
    wrap.dataset.seg = String(segIndex);

    const startSel = this.buildSelect('start', segIndex, view);
    const endSel = this.buildSelect('end', segIndex, view);
    if (seg.start) startSel.value = seg.start;
    if (seg.end) endSel.value = seg.end;

    const dash = document.createElement('span');
    dash.className = 'hours-table__dash';
    dash.setAttribute('aria-hidden', 'true');
    dash.textContent = '–';

    // 移除鈕（×）：段數 ≥2 顯示，=1 隱藏（CSS 由 __seg-remove + data 控）
    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'hours-table__seg-remove';
    removeBtn.setAttribute(
      'aria-label',
      `移除 ${this.day.label} 第${segIndex + 1}個時段`
    );
    removeBtn.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
      '<path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
    removeBtn.addEventListener('click', () => this.removeSegment(segIndex));

    wrap.append(startSel, dash, endSel, removeBtn);
    return wrap;
  }

  /* 把單一資料源渲染進指定視圖的 .hours-table__time 容器 */
  renderView(view) {
    const root = view === 'card' ? this.card : this.tableRow;
    if (!root) return;
    const timeWrap = root.querySelector('.hours-table__time');
    if (!timeWrap) return;

    // 清空既有內容（含舊靜態 select / dash / note / add 鈕）
    timeWrap.textContent = '';

    // 例外態收合：唯公休（closed）時段欄整個換一句話（無段）。資料源 this.segments 保留，
    // 切回帶段態由 setMode 復現。預約制（appt）不再收合 —— 與營業同走下方帶段渲染路徑，
    // 僅 seg-list 最前 prepend 常駐「需預約」marker（狀態語意，非淡出殘段）。
    if (this.mode === 'closed') {
      // 公休：中性灰字「公休」note，無段（取代舊「殘 select + opacity:.55」淡出）
      timeWrap.classList.add('is-closed');
      timeWrap.classList.remove('is-byappt');
      timeWrap.classList.remove('has-appt-flag');
      // 列層降權 hook（方向甲：狀態驅動）。整列套 is-closed-row → CSS 降權整列（底霧 +
      // 日期/「公休」字 ink-tertiary，無左緣 accent，列高/圓角不變）。雙 DOM 各自於 renderView 套。
      root.classList.add('is-closed-row');
      const note = document.createElement('span');
      note.className = 'hours-table__note hours-table__note--closed';
      note.textContent = '公休';
      timeWrap.appendChild(note);
      // act 區：公休仍可「複製上一天」，但無「＋新增時段」。
      this.placeRowActions(root, false, view);
      // 前台鉤子（h）：closed 容器移除 appt 標記（雙保險）
      this.markApptHooks(false);
      return;
    }

    // 帶段態（營業 / 預約制）：渲染段
    timeWrap.classList.remove('is-closed');
    // 列層降權 hook 移除（帶段態回純白，與平日無異）。
    root.classList.remove('is-closed-row');
    // is-byappt / has-appt-flag 保留作 appt 視覺語境鉤子（前台 / 未來可用；後台本頁無樣式依賴）。
    timeWrap.classList.toggle('is-byappt', this.mode === 'appt');
    timeWrap.classList.toggle('has-appt-flag', this.mode === 'appt');

    // 預約制：在 seg-list 之前掛常駐「需預約」marker（time 群組最前、select 之前）。
    // 純文字可被 SR 讀（非 aria-hidden）。桌機 time 改 flex-row → marker 與起訖同行
    // （閱讀序 週幾→預約制→需預約→起訖）；手機 time flex-column → marker 獨立一行在 seg-list 上。
    if (this.mode === 'appt') {
      const flag = document.createElement('span');
      flag.className = 'hours-table__appt-flag';
      flag.setAttribute('aria-label', '需預約');
      flag.textContent = '需預約'; // rone 定，勿改字
      timeWrap.appendChild(flag);
    }

    // 段堆疊容器
    const segList = document.createElement('div');
    segList.className = 'hours-table__seg-list';

    this.segments.forEach((seg, i) => {
      const segEl = this.buildSegment(seg, i, view);
      // 段數 = 1 → 隱藏移除鈕（要關整天用公休 radio）
      if (this.segments.length < 2) {
        const rm = segEl.querySelector('.hours-table__seg-remove');
        if (rm) rm.hidden = true;
      }
      segList.appendChild(segEl);
    });
    timeWrap.appendChild(segList);

    // act 區：擺「＋新增時段」（帶段態且 <3 段）+「複製上一天」（hover-reveal 群組）。
    this.placeRowActions(root, this.segments.length < MAX_SEGMENTS, view);

    // 前台鉤子（h）：帶段態時依 appt 與否標記容器（雙保險，前台用 [data-hours-mode] 選取）。
    this.markApptHooks(this.mode === 'appt');

    // 進場動畫：只對「剛新增」的最後一段觸發（使用者動作後）
    if (this._animateNextRender && hasSegments(this.mode) && this.segments.length) {
      const segs = segList.querySelectorAll('.hours-table__seg');
      const last = segs[segs.length - 1];
      if (last && !prefersReducedMotion()) {
        last.classList.add('is-entering');
        // 強制 reflow 後移除 → 觸發 transition（CSS 定義 .is-entering 起始態）
        void last.offsetHeight;
        requestAnimationFrame(() => last.classList.remove('is-entering'));
      }
    }
  }

  /* act 區（列動作群組，緊湊四區版）：把「複製上一天」與「＋新增時段」收進列尾單一
   * .hours-table__act 直接子容器（grid-area:act），桌機 hover-reveal、手機常駐。
   *   - 容器：取現有 .hours-table__act 或建一個，置於 root（grid 列）末尾作直接 grid 子項。
   *   - 複製上一天：HTML 靜態節點原在 .hours-table__daycol 內（週二起）；首次 render 用 appendChild
   *     搬進 act（appendChild 是移動，冪等可重入），讓它與 add 同列垂直置中、不再浮在日名下方。
   *   - ＋新增時段：每次 render 重建（其顯隱依段數變動），先移除舊的再依 includeAdd 補。
   * 不更動 copy-prev 的 class / data-hours-copy-prev / 既有 click 委派（init 以 closest 綁定，
   * 與 DOM 位置無關），契約完整保留。 */
  placeRowActions(root, includeAdd, view) {
    let act = root.querySelector(':scope > .hours-table__act');
    if (!act) {
      act = document.createElement('div');
      act.className = 'hours-table__act';
      root.appendChild(act);
    }
    // 複製上一天：若仍在 act 之外（首次 / 靜態位置），搬進 act 最前。
    const copyPrev = root.querySelector('.hours-copy-prev');
    if (copyPrev && copyPrev.parentElement !== act) {
      act.insertBefore(copyPrev, act.firstChild);
    }
    // ＋新增時段：重建。
    const oldAdd = act.querySelector('.hours-table__add');
    if (oldAdd) oldAdd.remove();
    if (includeAdd && hasSegments(this.mode)) {
      const addBtn = document.createElement('button');
      addBtn.type = 'button';
      addBtn.className = 'hours-table__add';
      // icon-only 圓鈕：無可見文字，無障礙名稱由 aria-label 承載（緊湊四區版）。
      addBtn.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
        '<path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
      addBtn.setAttribute('aria-label', `為 ${this.day.label} 新增時段`);
      addBtn.addEventListener('click', () => this.addSegment(view));
      act.appendChild(addBtn);
    }
    // act 群組是否有內容（決定 CSS 是否保留欄寬 / hover-reveal）。
    act.classList.toggle('is-empty', act.children.length === 0);
  }

  /* 前台鉤子（§B(h)）：在兩視圖該日容器掛 data-hours-mode="appt" + class is-appt（雙保險），
   * 讓前台 session 用 [data-hours-mode="appt"] / .is-appt 選取掛 inline 標籤＋警語，
   * 無需解析時間文字。非 appt 時清除兩者。後台本頁無前台樣式依賴此鉤子，純資料語意。 */
  markApptHooks(isAppt) {
    [this.tableRow, this.card].forEach((root) => {
      if (!root) return;
      root.classList.toggle('is-appt', isAppt);
      if (isAppt) root.dataset.hoursMode = 'appt';
      else delete root.dataset.hoursMode;
    });
  }

  /* 兩視圖同步渲染（單一資料源 → 兩 DOM） */
  render() {
    this.renderView('table');
    this.renderView('card');
    this._animateNextRender = false;
    this.syncModeRadios();
  }

  /* 把 mode 寫回兩套 radiogroup（保持雙 DOM 勾選一致） */
  syncModeRadios() {
    [this.tableRow, this.card].forEach((root) => {
      if (!root) return;
      root.querySelectorAll('.hours-table__status input').forEach((input) => {
        input.checked = input.value === this.mode;
      });
    });
  }

  /* 從單一視圖的 select 讀回值寫進資料源 */
  readSegmentsFromView(view) {
    const root = view === 'card' ? this.card : this.tableRow;
    if (!root) return;
    const segEls = root.querySelectorAll('.hours-table__seg');
    if (!segEls.length) return;
    const segs = [];
    segEls.forEach((segEl) => {
      const start = segEl.querySelector('select[data-role="start"]');
      const end = segEl.querySelector('select[data-role="end"]');
      segs.push({ start: start ? start.value : '', end: end ? end.value : '' });
    });
    this.segments = segs;
  }

  /* 從「當前可見」視圖讀回資料源（單一資料源唯一正確的回讀來源）。
   * 桌機可見=table、手機可見=cards；隱藏視圖的 select 是上次 render 的鏡像、未承接使用者輸入，
   * 回讀它會用 stale 值覆蓋剛輸入的可見視圖（雙 DOM 回讀「後者覆蓋」bug 根因）。
   * 用 offsetParent===null 判隱藏；兩者皆隱（罕見：容器被收合）時退回 table。 */
  readFromVisibleView() {
    const tableVisible = this.tableRow && this.tableRow.offsetParent !== null;
    const cardVisible = this.card && this.card.offsetParent !== null;
    if (cardVisible && !tableVisible) {
      this.readSegmentsFromView('card');
    } else {
      this.readSegmentsFromView('table');
    }
  }

  /* ---- 互動：新增段 ---- */
  addSegment(originView) {
    if (!hasSegments(this.mode) || this.segments.length >= MAX_SEGMENTS) return;
    // 先把當前視圖值同步進資料源（避免未提交的手動選擇被覆蓋）
    this.readSegmentsFromView(originView);
    this.segments.push({ start: '', end: '' });
    this._animateNextRender = true;
    this.render();

    // focus 落新段第一個 select（以觸發來源視圖為準）
    const root = originView === 'card' ? this.card : this.tableRow;
    if (root) {
      const segs = root.querySelectorAll('.hours-table__seg');
      const last = segs[segs.length - 1];
      const firstSelect = last?.querySelector('select[data-role="start"]');
      if (firstSelect) firstSelect.focus();
    }
    this.announce(`已新增 ${this.day.label} 第${this.segments.length}個時段`);
  }

  /* ---- 互動：移除段（空段直接移除；有值段 inline-undo） ---- */
  removeSegment(segIndex) {
    if (this.segments.length < 2) return; // =1 不可移（用公休）
    // 先把可見視圖最新值同步進資料源（不可回讀隱藏視圖：值為 stale 鏡像）
    this.readFromVisibleView();
    const removed = this.segments[segIndex];
    const hadValue = removed && (removed.start || removed.end);
    const ordinal = segIndex + 1;

    this.segments.splice(segIndex, 1);
    this.render();
    this.announce(`已移除 ${this.day.label} 第${ordinal}個時段`);

    // 有值段 → inline-undo（不彈 Modal）
    if (hadValue && this.onUndoToast) {
      this.onUndoToast({
        title: `已移除第 ${ordinal} 個時段`,
        text: `${this.day.label} ${removed.start || ''}${removed.end ? '–' + removed.end : ''}，可按復原還原。`,
        onUndo: () => {
          this.segments.splice(Math.min(segIndex, this.segments.length), 0, removed);
          this.render();
          this.announce(`已復原 ${this.day.label} 第${ordinal}個時段`);
        },
      });
    }
  }

  /* ---- mode 切換（radio change） ---- */
  setMode(mode) {
    if (mode === this.mode) return;
    // 切離帶段態（營業 / 預約制）前先存段值（只讀可見視圖）；切入帶段態復現 ≥1 段。
    if (hasSegments(this.mode)) {
      this.readFromVisibleView();
    }
    this.mode = mode;
    if (hasSegments(mode) && this.segments.length === 0) {
      this.segments = [{ start: '', end: '' }];
    }
    this.render();
  }

  /* ---- 複製來源天（複製上一天 / 套用平日 / 套用全部 共用） ---- */
  copyFrom(srcDay) {
    // 來源天可能有「使用者剛 native 改了 select 但未經 re-render」的值；先把來源可見視圖
    // 同步進其資料源，否則複製到的是 stale 的種子值（mode 已由 radio change→setMode 保持同步）。
    // 帶段態（營業 / 預約制）都有可編輯 select，皆需回讀；公休無段不需。
    if (hasSegments(srcDay.mode)) srcDay.readFromVisibleView();
    this.mode = srcDay.mode;
    this.segments = srcDay.segments.map((s) => ({ start: s.start, end: s.end }));
    if (hasSegments(this.mode) && this.segments.length === 0) {
      this.segments = [{ start: '', end: '' }];
    }
    this.render();
  }

  /* 快照 / 還原（套用平日/全部的 inline-undo 用） */
  snapshot() {
    return { mode: this.mode, segments: this.segments.map((s) => ({ ...s })) };
  }
  restore(snap) {
    this.mode = snap.mode;
    this.segments = snap.segments.map((s) => ({ ...s }));
    this.render();
  }

  /* highlight 回饋（複製/套用後短暫高亮兩視圖列） */
  highlight() {
    [this.tableRow, this.card].forEach((root) => {
      if (!root || prefersReducedMotion()) return;
      root.classList.remove('is-flash');
      void root.offsetHeight;
      root.classList.add('is-flash');
      window.setTimeout(() => root.classList.remove('is-flash'), 900);
    });
  }

  announce(msg) {
    if (this._liveRegion) this._liveRegion.textContent = msg;
  }
  setLiveRegion(el) {
    this._liveRegion = el;
  }

  /* ---- 驗證：回傳該天錯誤陣列 ---- */
  validate() {
    // 只讀「當前可見」視圖（與 removeSegment / setMode 一致）。先讀 table 再讀 card 的雙讀
    // 會用隱藏視圖的 stale 鏡像覆蓋可見視圖剛輸入的值（readFromVisibleView 註解所述「後者覆蓋」bug）。
    this.readFromVisibleView();
    const errors = []; // {segIndex|null, message}
    if (this.mode === 'closed') return errors; // 唯公休跳驗；預約制保留時間，照驗 end>start / 必選 / 不重疊

    // submit 前自動依 start 升序排序（遞增靠排序消化，不報錯）
    this.sortSegments();

    if (this.segments.length === 0) {
      errors.push({ segIndex: null, message: '請至少設定一個營業時段' }); // 需文案
      return errors;
    }

    // 逐段：start/end 必選 + end>start
    this.segments.forEach((seg, i) => {
      const s = toMinutes(seg.start);
      const e = toMinutes(seg.end);
      if (s == null || e == null) {
        errors.push({ segIndex: i, message: '請選擇開始與結束時間' }); // 需文案
        return;
      }
      if (e <= s) {
        errors.push({ segIndex: i, message: '結束時間需晚於開始時間' }); // 需文案
      }
    });

    // 跨段重疊（已排序）：前段 end > 後段 start → 重疊（跨日層級錯誤）
    for (let i = 1; i < this.segments.length; i += 1) {
      const prevEnd = toMinutes(this.segments[i - 1].end);
      const curStart = toMinutes(this.segments[i].start);
      if (prevEnd != null && curStart != null && curStart < prevEnd) {
        errors.push({ segIndex: null, message: '營業時段不可重疊' }); // 需文案
        break;
      }
    }
    return errors;
  }

  /* 依 start 升序排序段（null 值排末），並重渲染反映新順序 */
  sortSegments() {
    const before = this.segments.map((s) => s.start).join(',');
    this.segments.sort((a, b) => {
      const am = toMinutes(a.start);
      const bm = toMinutes(b.start);
      if (am == null) return 1;
      if (bm == null) return -1;
      return am - bm;
    });
    const after = this.segments.map((s) => s.start).join(',');
    if (before !== after) this.render();
  }

  /* 渲染錯誤到 DOM（段內錯誤貼段下、跨段錯誤貼該天下，role=alert） */
  renderErrors(errors) {
    this.clearErrors();
    [this.tableRow, this.card].forEach((root) => {
      if (!root) return;
      const timeWrap = root.querySelector('.hours-table__time');
      if (!timeWrap) return;
      const segEls = root.querySelectorAll('.hours-table__seg');

      errors.forEach((err) => {
        if (err.segIndex != null) {
          // 段內錯誤：標該段 select 紅 + error 貼段下
          const segEl = segEls[err.segIndex];
          if (!segEl) return;
          // 包一層 arrow：forEach 會傳 (el, index)；index 不可當 setInvalid 的 message 傳入。
          segEl.querySelectorAll('select').forEach((sel) => setInvalid(sel));
          const errEl = document.createElement('p');
          errEl.className = 'hours-table__error';
          errEl.setAttribute('role', 'alert');
          errEl.textContent = err.message;
          segEl.appendChild(errEl);
        } else {
          // 跨段 / 該天層級錯誤：貼 time 容器尾
          const errEl = document.createElement('p');
          errEl.className = 'hours-table__error hours-table__error--day';
          errEl.setAttribute('role', 'alert');
          errEl.textContent = err.message;
          timeWrap.appendChild(errEl);
        }
      });
    });
  }

  clearErrors() {
    [this.tableRow, this.card].forEach((root) => {
      if (!root) return;
      root.querySelectorAll('select[aria-invalid]').forEach((sel) => clearInvalid(sel));
      root.querySelectorAll('.hours-table__error').forEach((el) => el.remove());
    });
  }

  /* 取得指定視圖第一個 select（捲動 focus 用） */
  firstSelect(view) {
    const root = view === 'card' ? this.card : this.tableRow;
    return root ? root.querySelector('select') : null;
  }
}

/* =====================================================================
 * 雙 DOM 提交去重（form-validation spec §7.2）：
 * 桌機 .hours-table 與手機 .hours-cards 同時在 form 內、name 不同。提交時把當前
 * display:none 那套整組 input/select disabled（disabled 不進 submit），只送可見套，
 * 避免送兩份營業時間。用 offsetParent 判可見（display:none 時為 null）。
 *
 * 注意：可見套內公休/預約制段的 select 由 render() 已設 disabled（本就不該送），
 * 此處只負責「整個隱藏視圖」的去重，不碰可見套（保留 render 設定的 mode 態）。
 * ===================================================================== */
function syncDualDomDisabled(tableEl, cardsEl) {
  // offsetParent === null 代表該元素或祖先 display:none（即當前隱藏的響應式視圖）
  const tableHidden = tableEl ? tableEl.offsetParent === null : true;
  const cardsHidden = cardsEl ? cardsEl.offsetParent === null : true;
  if (tableEl && tableHidden) {
    tableEl.querySelectorAll('select, input').forEach((el) => {
      el.disabled = true;
    });
  }
  if (cardsEl && cardsHidden) {
    cardsEl.querySelectorAll('select, input').forEach((el) => {
      el.disabled = true;
    });
  }
}

/* =====================================================================
 * init
 * ===================================================================== */
function init(root = document) {
  const form = root.querySelector('.admin-clinic-form');
  if (!form) return; // no-op

  const tableEl = form.querySelector('[data-hours="table"]');
  const cardsEl = form.querySelector('[data-hours="cards"]');
  if (!tableEl && !cardsEl) return; // no-op：非三段式 hours 頁

  // aria-live region（單一，整個 hours 區共用）
  let liveRegion = form.querySelector('[data-hours-live]');
  if (!liveRegion) {
    liveRegion = document.createElement('div');
    liveRegion.className = 'visually-hidden';
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('data-hours-live', '');
    (tableEl || cardsEl).parentNode.appendChild(liveRegion);
  }

  // inline-undo toast helper（沿用 AdminToast.action 復原）
  const onUndoToast = ({ title, text, onUndo }) => {
    if (!window.AdminToast || typeof window.AdminToast.show !== 'function') return;
    window.AdminToast.show({
      type: 'info',
      title,
      text,
      duration: 6000,
      action: { label: '復原', onClick: onUndo }, // 需文案
    });
  };

  // 建每天控制器
  /** @type {Record<string, HoursDay>} */
  const dayMap = {};
  DAYS.forEach((day) => {
    const tableRow = tableEl?.querySelector(`.hours-table__row[data-day="${day.key}"]`);
    const card = cardsEl?.querySelector(`.hours-card[data-day="${day.key}"]`);
    if (!tableRow && !card) return;
    const hd = new HoursDay({ day, tableRow, card, onUndoToast });
    hd.setLiveRegion(liveRegion);
    hd.hydrateFromDom();
    hd.render();
    dayMap[day.key] = hd;
  });

  // ---- mode radio change（兩套 radiogroup 任一變動 → setMode 同步） ----
  [tableEl, cardsEl].forEach((scope) => {
    if (!scope) return;
    scope.addEventListener('change', (e) => {
      const input = e.target;
      if (!input.matches('.hours-table__status input[type="radio"]')) return;
      const container = input.closest('[data-day]');
      const key = container?.dataset.day;
      if (key && dayMap[key]) dayMap[key].setMode(input.value);
    });
  });

  // ---- 複製上一天 ghost link（週二起） ----
  const bindCopyPrev = (scope) => {
    if (!scope) return;
    scope.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-hours-copy-prev]');
      if (!btn) return;
      e.preventDefault();
      const key = btn.dataset.hoursCopyPrev;
      const idx = DAYS.findIndex((d) => d.key === key);
      if (idx <= 0) return;
      const prevKey = DAYS[idx - 1].key;
      const cur = dayMap[key];
      const prev = dayMap[prevKey];
      if (!cur || !prev) return;
      cur.copyFrom(prev);
      cur.highlight();
      cur.announce(`已將 ${DAYS[idx - 1].label} 設定複製到 ${DAYS[idx].label}`);
    });
  };
  bindCopyPrev(tableEl);
  bindCopyPrev(cardsEl);

  // ---- CARD 標題列「套用至平日 / 套用至全部」：以週一為來源 ----
  const applyFrom = (targetKeys, label) => {
    const src = dayMap.mon;
    if (!src) return;
    // 快照所有 target（含未變動的，便於整批 undo）
    const snaps = {};
    targetKeys.forEach((k) => {
      if (dayMap[k]) snaps[k] = dayMap[k].snapshot();
    });
    targetKeys.forEach((k) => {
      if (k === 'mon') return; // 來源不動
      if (dayMap[k]) {
        dayMap[k].copyFrom(src);
        dayMap[k].highlight();
      }
    });
    liveRegion.textContent = `已將週一設定套用至${label}`;
    // 破壞性 → inline-undo（整批還原）
    if (window.AdminToast && typeof window.AdminToast.show === 'function') {
      window.AdminToast.show({
        type: 'info',
        title: `已套用至${label}`, // 需文案
        text: '以週一營業時間為來源套用。',
        duration: 6000,
        action: {
          label: '復原',
          onClick: () => {
            Object.keys(snaps).forEach((k) => {
              if (dayMap[k]) dayMap[k].restore(snaps[k]);
            });
            liveRegion.textContent = `已復原${label}營業時間`;
          },
        },
      });
    }
  };

  form.querySelectorAll('[data-hours-apply]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const scope = btn.dataset.hoursApply; // 'weekday' | 'weekend' | 'all'
      if (scope === 'weekday') applyFrom(WEEKDAY_KEYS, '平日（週一至週五）');
      else if (scope === 'weekend') applyFrom(WEEKEND_DAYS, '週末（週六、週日）');
      else if (scope === 'all') applyFrom(DAYS.map((d) => d.key), '全部');
    });
  });

  // ---- 批次套用下拉鈕（重設計 A）：toggle + aria-expanded + 外部點/Esc 關 + ↑↓/Enter 鍵盤 ----
  // 觸發實際套用的 [data-hours-apply] 由上方 handler 處理（零改）；此處只管「開關 + a11y」。
  const dropdown = form.querySelector('[data-hours-dropdown]');
  if (dropdown) {
    const trigger = dropdown.querySelector('[data-hours-dropdown-trigger]');
    const panel = dropdown.querySelector('[data-hours-dropdown-panel]');
    const items = () => Array.from(panel.querySelectorAll('[role="menuitem"]'));

    const isOpen = () => !panel.hidden;
    const openMenu = (focusFirst) => {
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      if (focusFirst) {
        const list = items();
        if (list.length) list[0].focus();
      }
    };
    const closeMenu = (returnFocus) => {
      if (panel.hidden) return;
      panel.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      if (returnFocus) trigger.focus();
    };

    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      if (isOpen()) closeMenu(false);
      else openMenu(false);
    });
    trigger.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openMenu(true);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const list = items();
        openMenu(false);
        if (list.length) list[list.length - 1].focus();
      } else if (e.key === 'Escape') {
        closeMenu(true);
      }
    });

    // 選後關回 trigger（套用動作由 [data-hours-apply] handler 同時觸發）。
    panel.addEventListener('click', (e) => {
      if (e.target.closest('[role="menuitem"]')) closeMenu(true);
    });
    // 面板內鍵盤：↑↓ 移動、Enter 觸發、Esc 關回、Tab 關（不困住焦點）。
    panel.addEventListener('keydown', (e) => {
      const list = items();
      const idx = list.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        list[(idx + 1) % list.length].focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        list[(idx - 1 + list.length) % list.length].focus();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        closeMenu(true);
      } else if (e.key === 'Tab') {
        closeMenu(false);
      }
    });

    // 外部點關（捕捉於 document，dropdown 外的任何點擊都收起）。
    document.addEventListener('click', (e) => {
      if (isOpen() && !dropdown.contains(e.target)) closeMenu(false);
    });
  }

  // ---- 提交：驗證 + 自動排序 + 雙 DOM 去重 ----
  // 把驗證掛在 capture 階段、早於頁面其他 submit handler，確保 hours 驗證先擋。
  form.addEventListener(
    'submit',
    (e) => {
      // 全天驗證
      let firstErrorSelect = null;
      let hasError = false;
      Object.keys(dayMap).forEach((key) => {
        const hd = dayMap[key];
        const errors = hd.validate(); // 內含自動排序
        hd.renderErrors(errors);
        if (errors.length) {
          hasError = true;
          if (!firstErrorSelect) {
            // 找可見視圖的第一個出錯 select 供捲動 focus
            const tableVisible = tableEl && tableEl.offsetParent !== null;
            firstErrorSelect =
              hd.firstSelect(tableVisible ? 'table' : 'card') ||
              hd.firstSelect(tableVisible ? 'card' : 'table');
          }
        }
      });

      if (hasError) {
        e.preventDefault();
        e.stopImmediatePropagation(); // 擋後續 submit handler（避免假成功）
        if (firstErrorSelect) {
          firstErrorSelect.scrollIntoView({
            behavior: prefersReducedMotion() ? 'auto' : 'smooth',
            block: 'center',
          });
          firstErrorSelect.focus({ preventScroll: true });
        }
        return;
      }

      // 通過 → 雙 DOM 去重（隱藏套整組 disabled，不送兩份）
      syncDualDomDisabled(tableEl, cardsEl);
    },
    true // capture：先於其他 submit handler
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}

export { init, generateTimeOptions };
